import { ExtendedUser, Language, Route, populateRoute } from "@repo/common"
import axios, { AxiosInstance, AxiosRequestConfig } from "axios"
import { FirebaseError, FirebaseOptions, initializeApp } from "firebase/app"
import {
  AuthErrorCodes,
  browserLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  onIdTokenChanged,
  signInWithEmailAndPassword as signInWithFirebase,
  signOut,
} from "firebase/auth"
import {
  ACTIVE_ACCOUNT_KEY,
  StoredAccount,
  createAccountRegistry,
} from "./account-registry"
import {
  SessionLostReason,
  attachAuthInterceptors,
  attachStaleClientReload,
} from "./api-interceptors"
import {
  MissingPasswordError,
  TooManyFailedLoginAttemptsError,
  UserNotFoundError,
  WrongPasswordError,
} from "./errors"
import { createIdTokenSource } from "./id-token-source"

/** The slice of Sentry the auth layer reports to. */
export interface AuthReporter {
  captureException: (error: Error) => void
  captureMessage: (
    message: string,
    context: { level: "warning"; tags: Record<string, string> },
  ) => void
  flush: (timeoutMs: number) => Promise<boolean>
  setUser: (user: { id: string; email: string }) => void
}

const noopReporter: AuthReporter = {
  captureException: () => {},
  captureMessage: () => {},
  flush: async () => true,
  setUser: () => {},
}

export type AuthRequest = <T>(options: AxiosRequestConfig) => Promise<T>

export interface WebAuthConfig {
  /** Prefixes the "session lost" Sentry message, e.g. "CRM". */
  appName: string
  apiUrl: string
  firebase: FirebaseOptions
  authEmulatorHost?: string
  /** Omitted: requests never trigger a stale-client reload. */
  isClientStale?: () => Promise<boolean>
  reporter?: AuthReporter
  /** Store selection to remember per logged-in account. CRM only. */
  storeScope?: {
    getStoreId: () => Promise<string | null>
    setStoreId: (storeId: string) => Promise<void>
  }
  /**
   * Runs after sign-in, before the account is registered. Throwing aborts the
   * login; the caller is responsible for logging out again.
   */
  onLogin?: (
    user: ExtendedUser,
    context: { authRequest: AuthRequest },
  ) => Promise<void>
}

const toRequest =
  (client: AxiosInstance): AuthRequest =>
  async <T>(options: AxiosRequestConfig) => {
    const res = await client.request<T>(options)
    return res.data
  }

/**
 * Firebase auth, the token cache, multi-account storage and the API clients.
 * Registers global listeners and the default Firebase app, so each app must
 * call this exactly once.
 */
export const createWebAuth = (config: WebAuthConfig) => {
  const reporter = config.reporter ?? noopReporter
  const isClientStale = config.isClientStale ?? (async () => false)
  const baseURL = `${config.apiUrl}/v1`

  // A separate axios instance for auth-related API calls. Its interceptors are
  // attached at the bottom, once findIdToken and handleSessionLost are defined.
  const authApiClient = axios.create({ baseURL })

  /**
   * Client for endpoints that carry no `@Auth` decorator on the API — currently
   * the password-reset pair, which is called by definition while logged out.
   *
   * It must never require, refresh, or invalidate a session: routing these
   * calls through the auth interceptors logs the user out and navigates away
   * before the request is ever sent, which breaks password reset entirely.
   */
  const publicRequest = toRequest(
    attachStaleClientReload(axios.create({ baseURL }), { isClientStale }),
  )

  const app = initializeApp(config.firebase)

  // Not getAuth(): its default persistence prefers IndexedDB, so every page
  // load moved the session from localStorage into IndexedDB and back. A browser
  // whose IndexedDB refuses writes (seen as app/idb-set warnings) could lose
  // the signed-in user on that round trip.
  const auth = initializeAuth(app, { persistence: browserLocalPersistence })

  const emulatorHost = config.authEmulatorHost
  const emulatorUrl = emulatorHost
    ? emulatorHost.startsWith("http")
      ? emulatorHost
      : `http://${emulatorHost}`
    : null
  if (emulatorUrl) {
    connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true })
  }

  // The emulator serves securetoken under its own host. Without this, the REST
  // fallback refreshes emulator sessions against real Google, which rejects them.
  const secureTokenUrl = emulatorUrl
    ? `${emulatorUrl}/securetoken.googleapis.com/v1/token`
    : "https://securetoken.googleapis.com/v1/token"

  /**
   * Wait for the SDK to rehydrate its session from localStorage.
   *
   * `auth.currentUser` is null for the first few hundred ms of every page load
   * while the SDK reads its session asynchronously. Reading it without awaiting
   * this is the cold-load race that made requests go out with no Authorization
   * header at all.
   *
   * A failed rehydration must not reject: the token source can still refresh
   * over REST with the refresh token we keep ourselves.
   */
  const authReady = auth.authStateReady().catch((error) => {
    console.error("Failed to initialise Firebase auth:", error)
  })

  /** What the token source last gave as the cause of a lost session. */
  let tokenLossReason: string | null = null

  const idTokenSource = createIdTokenSource({
    storage: localStorage,
    authReady,
    getCurrentUser: () => auth.currentUser,
    refreshOverRest: async (refreshToken) => {
      const {
        data: { id_token, refresh_token },
      } = await axios.post<{ id_token: string; refresh_token: string }>(
        `${secureTokenUrl}?key=${config.firebase.apiKey}`,
        {
          grant_type: "refresh_token",
          refresh_token: refreshToken,
        },
      )
      return { idToken: id_token, refreshToken: refresh_token }
    },
    reportError: reporter.captureException,
    reportSessionEnd: (reason) => {
      tokenLossReason = reason
    },
  })

  const { storeAuthTokens, clearAuthTokens, findIdToken } = idTokenSource

  const accountRegistry = createAccountRegistry(localStorage)

  const readActiveTokens = (): {
    idToken: string
    refreshToken: string
  } | null => {
    try {
      const idToken = localStorage.getItem("idToken")
      const refreshToken = localStorage.getItem("refreshToken")
      return idToken && refreshToken ? { idToken, refreshToken } : null
    } catch {
      return null
    }
  }

  /**
   * Copies the active session — whose tokens the SDK and token source keep
   * fresh on the legacy keys — into the registry, so it survives being
   * replaced by another account.
   */
  const saveActiveAccount = async (name?: string): Promise<void> => {
    const email = accountRegistry.getActiveEmail()
    const tokens = readActiveTokens()
    if (!email || !tokens) return

    accountRegistry.upsert({
      email,
      name: name ?? accountRegistry.find(email)?.name ?? email,
      ...tokens,
      storeId: (await config.storeScope?.getStoreId()) ?? null,
    })
  }

  /** Records the session the app is running as, e.g. one from before accounts existed. */
  const registerActiveAccount = async (user: ExtendedUser): Promise<void> => {
    if (!readActiveTokens()) return
    accountRegistry.setActiveEmail(user.email)
    await saveActiveAccount(`${user.firstName} ${user.lastName}`)
  }

  /**
   * Makes a stored account the active session and reloads, which is the one
   * reliable way to drop every cache (queries, zustand, Sentry user) built for
   * the previous account.
   */
  const activateAccount = async (account: StoredAccount): Promise<void> => {
    // The SDK holds at most one user; left signed in, the token source would
    // keep minting tokens for the previous account. The stored refresh token
    // is all the token source needs to continue as this one.
    await signOut(auth).catch((error) => {
      console.error("Failed to sign out of Firebase:", error)
    })
    storeAuthTokens({
      idToken: account.idToken,
      refreshToken: account.refreshToken,
    })
    // Without a stored store the current one is kept; the app falls back to
    // the account's first store if it has no access to it.
    if (account.storeId) await config.storeScope?.setStoreId(account.storeId)
    accountRegistry.setActiveEmail(account.email)
    window.location.assign("/")
  }

  const listAccounts = (): StoredAccount[] => accountRegistry.list()

  const getActiveAccountEmail = (): string | null =>
    accountRegistry.getActiveEmail()

  const switchAccount = async (email: string): Promise<void> => {
    if (email === accountRegistry.getActiveEmail()) return
    await saveActiveAccount()
    const account = accountRegistry.find(email)
    if (!account) return
    await activateAccount(account)
  }

  /** Forgets an inactive account on this device. */
  const removeAccount = (email: string): void => {
    if (email === accountRegistry.getActiveEmail()) return
    accountRegistry.remove(email)
  }

  const logout = async (): Promise<void> => {
    // Sign-out removes the SDK's session from storage, which can throw when
    // the browser refuses access. A rejection here would strand
    // handleSessionLost after it has latched, leaving the tab hanging instead
    // of on /login.
    await signOut(auth).catch((error) => {
      console.error("Failed to sign out of Firebase:", error)
    })
    clearAuthTokens()

    const email = accountRegistry.getActiveEmail()
    if (email) accountRegistry.remove(email)
    accountRegistry.setActiveEmail(null)
  }

  /**
   * Logs out only the account in use. Another logged-in account takes over if
   * there is one; otherwise the device ends up on the login page.
   */
  const logoutActiveAccount = async (): Promise<void> => {
    await logout()
    const [nextAccount] = accountRegistry.list()
    if (nextAccount) {
      await activateAccount(nextAccount)
      return
    }
    window.location.assign("/login")
  }

  /**
   * Mirror every token the SDK mints into localStorage. The SDK already
   * refreshes in the background shortly before the 1-hour expiry; without this
   * listener that fresh token never reaches our cache, so the stale cache
   * actively defeats the SDK's own refresh. This is what makes expiry
   * invisible to the user.
   */
  onIdTokenChanged(auth, (user) => {
    // Never clear on null. This fires with null on boot before the session
    // rehydrates, and the e2e backdoor has no Firebase user at all — logout()
    // is the only thing allowed to remove tokens.
    if (!user) return

    user
      .getIdToken()
      .then((idToken) => {
        storeAuthTokens({ idToken, refreshToken: user.refreshToken })
      })
      .catch((error) => {
        console.error("Failed to sync ID token:", error)
      })
  })

  /**
   * Another tab's logout only clears the shared storage; without this, this
   * tab's in-memory tokens would outlive it until they expire. The event fires
   * in other tabs only, and with a null key when storage is cleared wholesale.
   */
  window.addEventListener("storage", (event) => {
    // Another tab switched account: this tab's in-memory token and caches
    // still belong to the previous one.
    if (
      event.key === ACTIVE_ACCOUNT_KEY &&
      event.newValue !== null &&
      event.newValue !== event.oldValue
    ) {
      window.location.reload()
      return
    }

    if (event.key !== null && event.key !== "idToken") return
    if (event.newValue !== null) return

    idTokenSource.handleStoredTokensRemoved()
  })

  /**
   * The single unrecoverable exit. The latch stops N parallel failing queries
   * from racing N navigations.
   *
   * handleSessionLost returns whether a navigation is under way. Callers rely
   * on this: they may only swallow the error when the page is genuinely about
   * to be replaced, otherwise a request would hang forever instead of failing.
   */
  let sessionLostHandled = false

  /**
   * Every forced logout leaves a Sentry event saying why, flushed before the
   * navigation that would otherwise drop it.
   */
  const reportSessionLost = async (
    reason: SessionLostReason,
  ): Promise<void> => {
    reporter.captureMessage(`${config.appName} session lost`, {
      level: "warning",
      tags: { reason, tokenLossReason: tokenLossReason ?? "none" },
    })
    await reporter.flush(2000)
  }

  /**
   * For the route guard: finding no token on load is only worth reporting when
   * a session existed and was lost, not for a browser that was never logged in.
   */
  const reportTokenLostOnLoad = async (): Promise<void> => {
    if (!tokenLossReason) return
    await reportSessionLost("no-token-on-load")
  }

  const handleSessionLost = async (
    reason: SessionLostReason,
  ): Promise<boolean> => {
    if (sessionLostHandled) return true
    if (window.location.pathname.startsWith("/login")) return false

    sessionLostHandled = true
    await reportSessionLost(reason)
    // Not handed to another logged-in account: the person should notice that
    // this account needs a new login rather than silently continue as someone
    // else.
    const email = accountRegistry.getActiveEmail()
    await logout()

    const target = `${window.location.pathname}${window.location.search}`
    const emailParam = email ? `&email=${encodeURIComponent(email)}` : ""
    window.location.href = `/login?redirect=${encodeURIComponent(target)}${emailParam}`

    return true
  }

  const authRequest = toRequest(authApiClient)

  /**
   * The API identifies the user from the token, so this works whether or not
   * the Firebase SDK managed to restore its own user. Failures throw to the
   * error screen; only the API saying the user is gone ends the session.
   */
  const getCurrentUser = async (): Promise<ExtendedUser> => {
    const user = await authRequest<ExtendedUser>({
      url: Route.USER_CONTROLLER__FIND_ME_EXTENDED,
      method: "GET",
    })

    reporter.setUser({ id: user.id, email: user.email })
    await registerActiveAccount(user)

    return user
  }

  // Public endpoint, and called from /reset-password while logged out — it
  // must not go through authApiClient, whose interceptors would redirect to
  // /login before the request is sent.
  const resetPassword = async (email: string): Promise<void> =>
    publicRequest<void>({
      url: populateRoute(Route.USER_CONTROLLER__RESET_PASSWORD, email),
      method: "PUT",
    })

  const handleFirebaseAuthError = (error: Error): never => {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case AuthErrorCodes.USER_DELETED:
          throw new UserNotFoundError()
        case AuthErrorCodes.INVALID_PASSWORD:
          throw new WrongPasswordError()
        case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
          throw new TooManyFailedLoginAttemptsError()
        case "auth/missing-password":
          throw new MissingPasswordError()
      }
    }
    throw error
  }

  const login = async (
    email: string,
    password: string,
  ): Promise<ExtendedUser> => {
    // Logging in while another account is active adds an account; keep the
    // current one's latest tokens so it can be switched back to.
    await saveActiveAccount()

    const userCredentials = await signInWithFirebase(
      auth,
      email,
      password,
    ).catch(handleFirebaseAuthError)

    const idToken = await userCredentials.user.getIdToken()
    storeAuthTokens({
      idToken,
      refreshToken: userCredentials.user.refreshToken,
    })

    const user = await getCurrentUser()
    await config.onLogin?.(user, { authRequest })
    await registerActiveAccount(user)

    return user
  }

  /**
   * A client for `@Auth`-guarded endpoints. Public endpoints must use
   * publicRequest instead — see the note there.
   *
   * Auth first, then stale-client: axios runs response interceptors in
   * registration order, and the stale-client `>= 400` branch would otherwise
   * fetch /version.json on every expired token before the 401 refresh runs.
   */
  const createApiClient = (options?: {
    getStoreId?: () => Promise<string | null>
    getLanguage?: () => Language
  }): { request: AuthRequest } => {
    const client = axios.create({ baseURL })
    attachAuthInterceptors(client, {
      findIdToken,
      handleSessionLost,
      getStoreId: options?.getStoreId,
      getLanguage: options?.getLanguage,
    })
    attachStaleClientReload(client, { isClientStale })
    return { request: toRequest(client) }
  }

  // The interceptors close over findIdToken and handleSessionLost, which must
  // be defined before this runs. Auth before stale-client, so an expired token
  // is refreshed silently instead of triggering a /version.json fetch first.
  attachAuthInterceptors(authApiClient, { findIdToken, handleSessionLost })
  attachStaleClientReload(authApiClient, { isClientStale })

  return {
    findIdToken,
    login,
    logout,
    logoutActiveAccount,
    getCurrentUser,
    resetPassword,
    handleSessionLost,
    reportTokenLostOnLoad,
    listAccounts,
    getActiveAccountEmail,
    switchAccount,
    removeAccount,
    createApiClient,
    publicRequest,
  }
}
